<script lang="ts">
  import Button from "../../components/atoms/Button.svelte";
  import Card from "../../components/atoms/Card.svelte";
  import DatePicker from "../../components/atoms/DatePicker.svelte";
  import Heading from "../../components/atoms/Heading.svelte";
  import Text from "../../components/atoms/Text.svelte";
  import InvoiceMethod from "../../components/molecules/InvoiceMethod.svelte";
  import MovingInformation from "../../components/organisms/MovingInformation.svelte";
  import PersonalInformation from "../../components/organisms/PersonalInformation.svelte";
  import type {
    Dates,
    Facility,
    FacilityInformation,
    FacilityValidation,
    InformationValidation,
    Invoice,
    Person,
    PersonValidation,
    TPropertiesResponse,
  } from "../../helpers/types";
  import { personValidator, validateFacility, validateInformation } from "../../helpers/validators";
  import { openChat } from "../../helpers/chat";
  import useMovingFlow from "../../providers/useMovingFlow";
  import MovingPowerOfAttorney from "../../components/organisms/MovingPowerOfAttorney.svelte";
  import DateInformationText from "../../components/atoms/DateInformationText.svelte";
  import { useRendered } from "../../helpers/hooks";
  import { useGetProperties } from "../../helpers/api";
  import { useQueryClient } from "@sveltestack/svelte-query";
  import { getFirstPossibleDate } from "../../helpers/helperMethods";
  import { onDestroy, onMount } from "svelte";
  import Loader from "../../components/atoms/Loader.svelte";

  const { state: store, send } = useMovingFlow();
  const client = useQueryClient();
  let { rendered } = useRendered();

  let { lastpossibledate } = $props();
  const lastPossibleDate = new Date(lastpossibledate);

  let facilityInformation = $state({ ...$store.context.facilityInformation });
  let facility: Facility = $state({ ...$store.context.facility });
  let selectedInvoice = $state({ ...$store.context.invoice });
  let isInvoiceSelected = $state($store.context.invoice.value ? true : false);
  let facilityValidator: FacilityValidation = $state({ valid: false, errors: {} });
  let personValidation: PersonValidation = $state({ valid: false, errors: {} });
  let informationValidator: InformationValidation = $state({ valid: false, errors: {} });
  let billingEmail = $state(
    $store.context.invoice?.label === "E-postadress" ? $store.context.invoice?.value : ""
  );
  let person: Person = $state({ ...$store.context.person });
  let personValid: boolean = $state(false);
  let invoiceMethodValid = $state(false);
  let validSocialSecurity: boolean = $state(false);
  let hasFetchedPersonAutomatic = $state($store.context.hasFetchedPersonAutomatic);
  let hasPressedContinue = $state(false);
  const disableInputs = $store.context.requiresLimePurchase;

  const isFacilityNumbersSet =
    typeof facility.preSetFacilityId === "string" && typeof facility.preSetAreaId === "string";

  if (isFacilityNumbersSet && !facility.facilityId && !facility.areaId) {
    facility.facilityId = facility.preSetFacilityId;
    facility.areaId = facility.preSetAreaId;
  }

  const params = {
    code: $store.context.campaign,
  };

  const startDate = $store.context.startDate;
  let date: Date = $state(startDate ? startDate : new Date());

  const resetInvoice = () => {
    billingEmail = "";
    selectedInvoice.value = "";
    isInvoiceSelected = false;
  };

  let firstPossible: Date = $state(new Date());
  let firstPossibleInDays: number = $state(21);

  const setFirstPossibleData = (properties: TPropertiesResponse) => {
    firstPossibleInDays = properties.result.movingInDays;
    firstPossible = getFirstPossibleDate(firstPossibleInDays);
  };

  const queryResult = useGetProperties(params, {
    staleTime: 10000,
    refetchOnWindowFocus: false,
    enabled: $rendered === true,
    onSuccess: (data) => {
      setFirstPossibleData(data);
    },
  });

  const clearPersonInfo = () => {
    const socialSecurity = person.socialSecurity;
    send("SET_HAS_FETCHED_PERSON_AUTOMATIC", { hasFetchedPersonAutomatic: false });
    hasFetchedPersonAutomatic = false;
    person = {};
    person = {
      socialSecurity: socialSecurity,
      ...person,
    };
    facilityInformation = {};
  };

  if (client.getQueryData(["properties", params])) {
    const propertiesResponse = client.getQueryData<TPropertiesResponse>(["properties", params]);
    setFirstPossibleData(propertiesResponse);
  }

  onMount(() => {
    if (!client.getQueryData(["properties", params])) $queryResult.refetch();
  });

  $effect(() => {
    facilityValidator = validateFacility(facility);
  });

  $effect(() => {
    informationValidator = validateInformation(facilityInformation);
  });

  const sendInformation = (
    facilityInformation: FacilityInformation,
    person: Person,
    selectedInvoice: Invoice,
    date: Date,
    facility: Facility
  ) => {
    send("SET_FACILITY_INFORMATION", { facilityInformation });
    send("SET_PERSON", { person });
    send("SET_INVOICE", { invoice: selectedInvoice });
    send("SET_START_DATE", { startDate: date });
    send("SET_FACILITY", { facility });
  };

  const fetchPersonAutomatic = () => {
    send("SET_HAS_FETCHED_PERSON_AUTOMATIC", { hasFetchedPersonAutomatic: true });
    hasFetchedPersonAutomatic = true;
  };

  onDestroy(() => {
    sendInformation(facilityInformation, person, selectedInvoice, date, facility);
  });

  const onClickNext = () => {
    if (!inputValid) return;

    sendInformation(facilityInformation, person, selectedInvoice, date, facility);
    send("NEXT");
  };

  $effect(() => {
    if (person) {
      personValidation = personValidator(person);
    }
  });

  const dateToString = (date: Date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000).toISOString().split("T")[0];

  let inputValid = $derived(
    facility.valid &&
      facilityInformation.valid &&
      personValid &&
      validSocialSecurity &&
      invoiceMethodValid
  );
</script>

<form on:submit|preventDefault={onClickNext}>
  <MovingInformation bind:hasPressedContinue bind:facilityInformation bind:informationValidator />
  <Card>
    <Heading variant="primary" class="mb-2" margin={false}
      >Fyll i tillträdesdatum för din inflyttning</Heading
    >
    {#if $queryResult.isError}
      <div class="flex space-y-4 flex-col my-4">
        <span class="text-Red900 mx-auto text-H5 leading-5 font-bold error-text block"
          >Lyckades inte hämta aktuella datum</span
        >
        <Button class="mx-auto" type="button" onClick={() => $queryResult.refetch()}
          >Försök igen</Button
        >
      </div>
    {:else if $queryResult.isLoading}
      <div class="flex">
        <div class="mx-auto p-4">
          <Loader size="big" />
        </div>
      </div>
    {:else if firstPossible && $queryResult.isSuccess && firstPossibleInDays}
      <div class="flex space-x-2">
        <DatePicker
          autoFocus={false}
          bind:date
          minDate={firstPossible}
          maxDate={lastPossibleDate}
        />
        <div class="my-auto">
          <DateInformationText days={firstPossibleInDays} />
        </div>
      </div>
    {/if}
  </Card>
  <Card>
    <MovingPowerOfAttorney {hasPressedContinue} bind:facilityValidator bind:facility />
  </Card>
  <PersonalInformation
    {clearPersonInfo}
    bind:hasFetchedPersonAutomatic
    {fetchPersonAutomatic}
    bind:hasPressedContinue
    {resetInvoice}
    {disableInputs}
    bind:validSocialSecurity
    bind:person
    bind:personValid
    requiresLimePurchase={$store.context.requiresLimePurchase}
  />
  <InvoiceMethod
    {person}
    bind:valid={invoiceMethodValid}
    bind:selectedInvoice
    billingEmail={billingEmail || person.email}
    bind:personMobile={person.mobile}
    campaign={$store.context.campaign}
    {isInvoiceSelected}
    disabled={!personValidation.valid}
  />
  {#if date && date < firstPossible}
    <Card class="fade-in">
      <Heading variant={"secondary"}>Kan inte gå vidare med detta datum</Heading>
      <Text class={"mb-2 text-M"} variant={"primary"}>
        Du måste kontakta oss för att göra beställningen med startdatum {dateToString(date)}.
        <br />
        Ring <a class={"font-bold"} href="tel:+46910772550">0910-77 25 50</a>, (vardagar) eller
        <button type="button" class={"font-bold"} on:click={openChat}>chatta med oss</button>.
      </Text>
      <Text variant={"primary"} class=" font-bold text-PurplePrimary">
        Det första möjliga datum för att fortsätta med ordern här är {dateToString(firstPossible)}.
      </Text>
    </Card>
  {/if}
  <Button
    disabled={$store.context.purchaseFlowType == "v1" ? !inputValid : false}
    onClick={() => {
      hasPressedContinue = true;
    }}
    animate={false}
    type={"submit"}
    class="w-full my-2">Fortsätt</Button
  >
</form>
